import { Features, ViewIds } from '@ardoq/api-types';
import { Team } from '@ardoq/profiling';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import {
  BubbleChartCommands,
  BubbleChartRenderingData,
  BubbleChartViewProperties,
  BubbleChartViewSettings,
  BubbleChartView,
  updateSelectedQuadrantViewSettings,
} from '@ardoq/bubble-chart';
import { isEqual } from 'lodash';
import { ViewpointModeBubbleChartSourceDataArgs } from './sourceData/buildViewpointModeViewModelSourceData';
import { context$ } from 'streams/context/context$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { activeDiffMode$ } from 'scope/activeDiffMode$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { featureContext$ } from 'streams/featureContext$';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import { isDiscoverJourney$ } from 'canvas/isDiscoverJourney';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { referenceLabelFilters$ } from 'streams/filters/referenceLabelFilters$';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  reportDataWorkStarted,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { ViewpointModeBubbleChartSourceDataLayer } from './ViewpointModeBubbleChartSourceDataLayer';
import { BubbleChartViewLegend } from './BubbleChartViewLegend';
import { updateViewContext, updateViewProperties } from '@ardoq/view-settings';
import { BubbleChartZoomControls } from './zoomControls/BubbleChartZoomControls';
import { Box } from '@ardoq/layout';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
} from 'rxjs';
import { shallowEqual } from 'utils/objectUtils';
import type { FunctionComponent } from 'react';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import {
  getViewSettingsForModelSourceData$,
  getViewSettingsForRenderingData$,
} from './streams/viewSettings$';
import {
  toViewpointModeSettingsControlsDependencies,
  toViewpointModeViewContext,
  type ViewpointModeBubbleChartSettingsControlsDependencies,
} from './viewpointMode/settingsControls';
import { startBubbleChartTracking } from './streams/performanceTrackingUtils';
import { createViewpointModeBubbleChartCommands } from './commands';

const VIEW_ID = ViewIds.BUBBLE;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const PerformanceTrackedBubbleChart = (props: BubbleChartViewProperties) => {
  usePerformanceTracking('bubble chart view render', 100, {
    viewId: VIEW_ID,
    team: Team.INSIGHT,
    metadata: { itemCount: props.data.length, isViewpointMode: true },
  });

  return <BubbleChartView {...props} />;
};

const publishSettingsControlsData = (
  dependencies: ViewpointModeBubbleChartSettingsControlsDependencies
) => {
  dispatchAction(
    updateViewProperties({
      viewId: VIEW_ID,
      viewProperties: dependencies,
    })
  );
  dispatchAction(updateViewContext(toViewpointModeViewContext(dependencies)));
};

const getSettingsControlsData = (
  previousState: BubbleChartRenderingData,
  state: BubbleChartRenderingData
): ViewpointModeBubbleChartSettingsControlsDependencies | null => {
  const previous = toViewpointModeSettingsControlsDependencies(previousState);
  const current = toViewpointModeSettingsControlsDependencies(state);

  return isEqual(previous, current) ? null : current;
};

const viewpointModeSettingsControlsHandlers = {
  getSettingsControlsData,
  publishSettingsControlsData,
};

const toSourceDataLayerProps = ([
  sourceDataArgs,
  viewSettingsForRenderingData,
  bubbleChartCommands,
]: [
  ViewpointModeBubbleChartSourceDataArgs & HasDataWorkToken,
  BubbleChartViewSettings,
  BubbleChartCommands,
]) => ({
  ...sourceDataArgs,
  viewSettingsForRenderingData,
  bubbleChartCommands,
});

const isQuickExploreSelectionEnabled = ([isDiscoverJourney, featureContext]: [
  boolean,
  FeatureContext,
]): boolean =>
  isDiscoverJourney ||
  featureOperations.hasFeature(featureContext, Features.CANVAS_PROTOTYPE);

const bubbleChartCommands$: Observable<BubbleChartCommands> = combineLatest([
  isDiscoverJourney$,
  featureContext$,
]).pipe(
  map(isQuickExploreSelectionEnabled),
  distinctUntilChanged(),
  map(createViewpointModeBubbleChartCommands)
);

const startTracking = (
  sourceDataArgs: ViewpointModeBubbleChartSourceDataArgs
): ViewpointModeBubbleChartSourceDataArgs => ({
  ...sourceDataArgs,
  trackingId: startBubbleChartTracking(),
});

const getSourceDataArgs$ = (
  viewSettingsForModelSourceData$: Observable<BubbleChartViewSettings>,
  viewStatusReporter: ViewStatusReporter
): Observable<ViewpointModeBubbleChartSourceDataArgs & HasDataWorkToken> =>
  combineLatest({
    context: context$,
    viewSettings: viewSettingsForModelSourceData$,
    loadedGraph: loadedGraph$,
    scopeDiffState: scopeDiff$,
    activeDiffMode: activeDiffMode$,
    permissionContext: currentUserPermissionContext$,
    locale: locale$,
    featureContext: featureContext$,
    i18nLocale: i18nLocale$,
    orgUsersById: orgUsersById$,
    formattingAndLabelRules: formattingAndLabelRules$,
    referenceLabelFilters: referenceLabelFilters$,
  }).pipe(
    distinctUntilChanged(shallowEqual),
    reportDataWorkStarted(viewStatusReporter, 'reset'),
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    map(startTracking),
    withDataWork(viewStatusReporter, 'source')
  );

const viewSettingsWithChanges$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  VIEW_INSTANCE_ID
);

const viewStatusReporter = createViewStatusReporter(VIEW_INSTANCE_ID);

const sourceDataLayerProps$ = combineLatest([
  getSourceDataArgs$(
    getViewSettingsForModelSourceData$(viewSettingsWithChanges$),
    viewStatusReporter
  ),
  getViewSettingsForRenderingData$(viewSettingsWithChanges$),
  bubbleChartCommands$,
]).pipe(
  map(args => ({ ...toSourceDataLayerProps(args), viewStatusReporter })),
  registerWhileSubscribed(viewStatusReporter)
);

const ConnectedSourceDataLayer = connect(
  ViewpointModeBubbleChartSourceDataLayer,
  sourceDataLayerProps$
);

const ViewpointModeBubbleChart: FunctionComponent = () => {
  return (
    <Box className="prevent-context-change" width="full" height="full">
      <ConnectedSourceDataLayer
        viewInstanceId={VIEW_INSTANCE_ID}
        settingsControlsHandlers={viewpointModeSettingsControlsHandlers}
        onRenderingDataChange={updateSelectedQuadrantViewSettings}
        BubbleChartComponent={PerformanceTrackedBubbleChart}
        ViewLegendComponent={BubbleChartViewLegend}
        ZoomControlsComponent={BubbleChartZoomControls}
      />
    </Box>
  );
};

export { ViewpointModeBubbleChart };
